import { AxiosResponse } from 'axios'
import { http } from '@api/RequestApi'

import {
  IMemberPayload,
  IGetMembersResponse,
  IGetMemberResponse,
  MemberImportAnalyzeApiEnvelope
} from '@store/features/member/member.types'

function clubsMemberImportBase(clubId: string): string {
  return `/clubs/${clubId}/members/import`
}

/** Large CSV analyze/commit can exceed default proxy timeouts without an explicit client budget. */
const memberImportLongRunningTimeoutMs = 600_000

class MemberService {
  private prefix = '/members'

  public async getMember({ id }: { id: string }) {
    return http.get<void, AxiosResponse<IGetMemberResponse>>(`${this.prefix}/${id}/info`)
  }

  public async getMembers(url: string) {
    return http.get<void, AxiosResponse<IGetMembersResponse>>(`${this.prefix}?${url}`)
  }

  public async getMembersByKey(data: { key: string }) {
    return http.get<void, AxiosResponse<IGetMembersResponse>>(`${this.prefix}?key=${data.key}`)
  }

  public async createMember(data: IMemberPayload) {
    return http.post<IMemberPayload, AxiosResponse<string>>(`${this.prefix}`, data)
  }

  public async uploadCsvMembers({ formData }: { formData: FormData }) {
    return http.post<FormData, AxiosResponse<string>>(`${this.prefix}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  /* eslint-disable class-methods-use-this -- club-scoped import URLs omit `this.prefix` */
  public async analyzeMemberImport({ clubId, formData }: { clubId: string; formData: FormData }) {
    return http.post<FormData, AxiosResponse<MemberImportAnalyzeApiEnvelope>>(
      `${clubsMemberImportBase(clubId)}/analyze`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: memberImportLongRunningTimeoutMs
      }
    )
  }

  public async commitMemberImport({
    clubId,
    body
  }: {
    clubId: string
    body: { analysisId: string; createRowKeys?: string[]; renotifyMemberIds?: string[] }
  }) {
    return http.post<typeof body, AxiosResponse<unknown>>(
      `${clubsMemberImportBase(clubId)}/commit`,
      body,
      { timeout: memberImportLongRunningTimeoutMs }
    )
  }

  public async downloadMemberImportExport({
    clubId,
    analysisId,
    cohort
  }: {
    clubId: string
    analysisId: string
    cohort: string
  }): Promise<Blob> {
    const qs = `analysisId=${encodeURIComponent(analysisId)}&cohort=${encodeURIComponent(cohort)}`
    const res = await http.get<Blob, AxiosResponse<Blob>>(
      `${clubsMemberImportBase(clubId)}/export?${qs}`,
      { responseType: 'blob' }
    )
    return res.data
  }

  /* eslint-enable class-methods-use-this */

  public async deleteMember(id: string) {
    return http.delete<void, AxiosResponse<string>>(`${this.prefix}/${id}`)
  }
}

export default MemberService
